/**
 * Availability Request Details Modal
 *
 * Read-only modal that surfaces the full details of an availability request
 * a recipient has either accepted, declined, or let expire without
 * responding. Opened from the "For You" cards on the Availability Requests
 * panel (Referrals page). The "Change Response" / "Decline" actions live on
 * the card itself; this modal is purely informational so the recipient can
 * review what was being asked of them.
 */

import { AlertCircle, Briefcase, Calendar, CheckCircle, Clock, MapPin, MessageSquare, User, XCircle } from 'lucide-react';
import { Modal } from './Modal';
import { Button } from './ui/button';

interface AvailabilityRequestDetails {
  id: string;
  senderName?: string | null;
  senderCompany?: string | null;
  jobDescription?: string | null;
  jobLocation?: string | null;
  expiresAt?: string | null;
  createdAt?: string | null;
  responseStatus?: 'pending' | 'available' | 'unavailable';
  responseMessage?: string | null;
  respondedAt?: string | null;
}

interface AvailabilityRequestDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  request: AvailabilityRequestDetails | null;
  /**
   * Invoked when the recipient wants to change their response (accepted ↔
   * declined). The parent typically opens the response modal in change mode.
   */
  onChangeResponse?: (request: AvailabilityRequestDetails) => void;
}

const formatTimeRemaining = (expiresAt: string | undefined | null): string => {
  if (!expiresAt) return 'No expiration';
  const expiry = new Date(expiresAt);
  const diff = expiry.getTime() - Date.now();
  if (diff < 0) return 'Expired';

  const hours = Math.floor(diff / (1000 * 60 * 60));
  if (hours < 1) return 'Less than 1 hour';
  if (hours === 1) return '1 hour remaining';
  if (hours < 24) return `${hours} hours remaining`;

  const days = Math.floor(hours / 24);
  return days === 1 ? '1 day remaining' : `${days} days remaining`;
};

const formatDateTime = (value: string | undefined | null): string | null => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

export const AvailabilityRequestDetailsModal = ({
  isOpen,
  onClose,
  request,
  onChangeResponse,
}: AvailabilityRequestDetailsModalProps) => {
  if (!request) return null;

  const expired = request.expiresAt ? new Date(request.expiresAt).getTime() < Date.now() : false;
  const senderLabel = [request.senderName, request.senderCompany].filter(Boolean).join(' · ') || 'Unknown sender';
  const respondedAtFormatted = formatDateTime(request.respondedAt);
  const sentAtFormatted = formatDateTime(request.createdAt);
  const expiresAtFormatted = formatDateTime(request.expiresAt);

  const accepted = request.responseStatus === 'available';
  const declined = request.responseStatus === 'unavailable';
  const expiredUnanswered = expired && request.responseStatus === 'pending';

  let banner: {
    bg: string;
    border: string;
    iconColor: string;
    titleColor: string;
    bodyColor: string;
    Icon: typeof CheckCircle;
    title: string;
    detail?: string | null;
  };
  if (accepted) {
    banner = {
      bg: 'bg-green-50',
      border: 'border-green-200',
      iconColor: 'text-green-600',
      titleColor: 'text-green-900',
      bodyColor: 'text-green-800',
      Icon: CheckCircle,
      title: 'You accepted this request',
      detail: respondedAtFormatted ? `Responded ${respondedAtFormatted}` : null,
    };
  } else if (declined) {
    banner = {
      bg: 'bg-gray-50',
      border: 'border-gray-200',
      iconColor: 'text-gray-500',
      titleColor: 'text-gray-900',
      bodyColor: 'text-gray-700',
      Icon: XCircle,
      title: 'You declined this request',
      detail: respondedAtFormatted ? `Responded ${respondedAtFormatted}` : null,
    };
  } else if (expiredUnanswered) {
    banner = {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      iconColor: 'text-amber-600',
      titleColor: 'text-amber-900',
      bodyColor: 'text-amber-800',
      Icon: AlertCircle,
      title: 'This request expired before you responded',
      detail: expiresAtFormatted ? `Expired ${expiresAtFormatted}` : null,
    };
  } else {
    banner = {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      iconColor: 'text-blue-600',
      titleColor: 'text-blue-900',
      bodyColor: 'text-blue-800',
      Icon: Clock,
      title: 'Awaiting your response',
      detail: expiresAtFormatted ? `Expires ${expiresAtFormatted}` : null,
    };
  }
  const BannerIcon = banner.Icon;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Availability Request"
      size="medium"
      showCloseButton
      closeOnOverlayClick
    >
      <div className="space-y-5">
        {/* Status banner */}
        <div className={`${banner.bg} border ${banner.border} rounded-lg p-3 flex items-start gap-2`}>
          <BannerIcon className={`h-5 w-5 ${banner.iconColor} mt-0.5 flex-shrink-0`} />
          <div>
            <p className={`text-sm font-semibold ${banner.titleColor}`}>{banner.title}</p>
            {banner.detail && (
              <p className={`text-xs ${banner.bodyColor} mt-0.5`}>{banner.detail}</p>
            )}
          </div>
        </div>

        {/* Sender */}
        <div className="flex items-start gap-2">
          <User className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-medium text-gray-900">From</h3>
            <p className="text-gray-700">{senderLabel}</p>
          </div>
        </div>

        {/* Job description */}
        <div className="flex items-start gap-2">
          <Briefcase className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-medium text-gray-900">Job Description</h3>
            <p className="text-gray-700 whitespace-pre-wrap break-words">
              {request.jobDescription || 'No description provided'}
            </p>
          </div>
        </div>

        {/* Location */}
        {request.jobLocation && (
          <div className="flex items-start gap-2">
            <MapPin className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-gray-900">Location</h3>
              <p className="text-gray-700">{request.jobLocation}</p>
            </div>
          </div>
        )}

        {/* Time window */}
        <div className="flex items-start gap-2">
          <Clock className={`h-5 w-5 mt-0.5 flex-shrink-0 ${expired ? 'text-red-500' : 'text-amber-500'}`} />
          <div>
            <h3 className="font-medium text-gray-900">Availability Window</h3>
            <p className="text-gray-700">{formatTimeRemaining(request.expiresAt)}</p>
          </div>
        </div>

        {/* Sent date */}
        {sentAtFormatted && (
          <div className="flex items-start gap-2">
            <Calendar className="h-5 w-5 text-gray-500 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-gray-900">Sent</h3>
              <p className="text-gray-700">{sentAtFormatted}</p>
            </div>
          </div>
        )}

        {/* Response message (if user added one) */}
        {request.responseMessage && (
          <div className="flex items-start gap-2">
            <MessageSquare className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <h3 className="font-medium text-gray-900">Your Message</h3>
              <p className="text-gray-700 italic whitespace-pre-wrap break-words">
                "{request.responseMessage}"
              </p>
            </div>
          </div>
        )}

        {/* Footer actions */}
        {!expired && (accepted || declined) && onChangeResponse && (
          <div className="border-t border-gray-200 pt-4 flex flex-col sm:flex-row gap-2">
            <Button
              onClick={() => onChangeResponse(request)}
              variant="outline"
              className={`flex-1 ${
                accepted
                  ? 'border-red-300 text-red-700 hover:bg-red-50'
                  : 'border-green-300 text-green-700 hover:bg-green-50'
              }`}
            >
              {accepted ? (
                <>
                  <XCircle className="w-4 h-4 mr-2" />
                  Change to Declined
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Change to Accepted
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AvailabilityRequestDetailsModal;
